import { Paragraph, StyledAlert } from "App/global-style";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  AdviceProductDetailsFormValues,
  DisabilityPension,
} from "./FormElements/interfaces";

import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { getAdviceStart } from "Store/advice/slice";
import { AdviceVersion } from "Store/akaAdvice/akaAdviceHooks";
import { getAkaAdviceStart } from "Store/akaAdvice/slice";
import { usePartnerId } from "Store/partners/partnerHooks";
import { Partner } from "Store/partners/partnerInterfaces";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { mapValidationErrors } from "Utils/formUtils";
import { isAkademikerneTheme } from "Utils/partnerUtils";
import React from "react";
import { ADVICE_YOUR_LIFE_SITUATION_STEP } from "..";
import { ProductBackendName } from "../../productTypes";
import { useAdviceVersion } from "../hooks";
import { EconomyFormElements } from "./FormElements/EconomyFormElements";
import { FamilyFormElements } from "./FormElements/FamilyFormElements";
import { WorkFormElements } from "./FormElements/WorkFormElements";
import { mapDetailsFormDataToAdviceData } from "./utils";
import { validationSchema } from "./validation";

export const ProductDetails = (): React.ReactElement => {
  const dispatch = useDispatch();
  const partnerId = usePartnerId();
  const initialFormState = useFormState(ADVICE_YOUR_LIFE_SITUATION_STEP.id, {});
  const adviceVersion = useAdviceVersion();
  const [{ formText, details }, lang] = useProductTexts(
    getFMSProductName(ProductBackendName.ADVICE),
  );
  const {
    advice: { adviceResult, advicePriceCalculationResults },
    akaAdvice: {
      adviceResult: akaAdviceResult,
      advicePriceCalculationResults: akaAdvicePriceCalculationResults,
    },
    userIdentification,
  } = useSelector((state: ReduxState) => state);

  const isAka = isAkademikerneTheme(partnerId as Partner);
  const isLoading =
    adviceResult.isLoading ||
    advicePriceCalculationResults.isLoading ||
    akaAdviceResult.isLoading ||
    akaAdvicePriceCalculationResults.isLoading;
  const customer = userIdentification.current?.newCustomerResponse;
  const customerDateOfBirth = customer?.dateOfBirth;
  const customerFirstName = customer?.providedPersonInfo?.name?.firstName ?? "";

  const handleSubmit = (form: AdviceProductDetailsFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: ADVICE_YOUR_LIFE_SITUATION_STEP.id,
        formState: form,
      }),
    );
    const backendValues = mapDetailsFormDataToAdviceData({
      ...form,
      dateOfBirth: customerDateOfBirth,
    });
    if (adviceVersion === AdviceVersion.OLD) {
      dispatch(getAdviceStart(backendValues));
    } else {
      dispatch(
        getAkaAdviceStart({
          adviceProductDetailsBackendValues: backendValues,
          isCollectiveAdvice: isAka,
        }),
      );
    }
  };

  return (
    <ProductDetailsTile
      header={`${formText.aboutYou.header[lang]} ${customerFirstName}`}
      validationSchema={validationSchema(formText.aboutYou, lang)}
      initialValues={initialFormState}
      onSubmit={handleSubmit}
    >
      {(formProps: FormikProps<AdviceProductDetailsFormValues>) => {
        return (
          <Form>
            <Paragraph>{formText.aboutYou.desc[lang]}</Paragraph>
            <WorkFormElements
              formProps={formProps}
              formText={formText}
              lang={lang}
            />
            <FamilyFormElements
              formProps={formProps}
              formText={formText}
              lang={lang}
            />
            <EconomyFormElements
              formProps={formProps}
              formText={formText}
              lang={lang}
            />
            <StyledAlert title={formText.aboutYou.edh[lang]} />
            {formProps.values.disabilityPension ===
              DisabilityPension.DO_NOT_KNOW && (
              <StyledAlert
                title={formText.aboutYou.disabilityPensionUnknown[lang]}
              />
            )}
            {adviceResult.error && <SystemErrorAlert alertId="adviceResult" />}
            {akaAdviceResult.error && (
              <SystemErrorAlert alertId="akaAdviceResult" />
            )}
            {formProps.submitCount > 0 && !formProps.isValid && (
              <ErrorsSummedUp
                errors={mapValidationErrors(formProps.errors, details, lang)}
                isVisible
              />
            )}
            <NavigationButtons isLoading={isLoading} />
          </Form>
        );
      }}
    </ProductDetailsTile>
  );
};
